/**
 * Auth state — minimal Zustand store wrapping localStorage tokens.
 * Adapted from PayrollSystem's stores/auth.ts, dropping the tenant slice.
 *
 * Tokens are the source of truth (set/cleared in lib/auth.ts). The store's
 * derived `user`/`role` are decoded from the access token on hydrate and on
 * `setSession`. After login, calling /me/ refreshes the email/display_name
 * fields (the JWT only carries user_id + role).
 */

import { create } from "zustand";

import { clearTokens, getAccessToken, setTokens } from "@/lib/auth";
import { decodeJWT, isExpired } from "@/lib/jwt";
import type { AuthSession, Role, User } from "@/types/api";

interface AuthState {
  user: User | null;
  role: Role | null;
  isHydrated: boolean;

  setSession: (s: AuthSession) => void;
  clearSession: () => void;
  hydrate: () => void;
  setUser: (u: User) => void; // for /me/ refresh
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  role: null,
  isHydrated: false,

  setSession: (s) => {
    setTokens(s.tokens.access, s.tokens.refresh);
    set({ user: s.user, role: s.role, isHydrated: true });
  },

  clearSession: () => {
    clearTokens();
    set({ user: null, role: null, isHydrated: true });
  },

  hydrate: () => {
    const token = getAccessToken();
    if (!token || isExpired(token)) {
      clearTokens();
      set({ user: null, role: null, isHydrated: true });
      return;
    }
    const claims = decodeJWT(token);
    if (!claims) {
      clearTokens();
      set({ user: null, role: null, isHydrated: true });
      return;
    }
    set({
      // We don't have email/display_name from the JWT — caller fetches
      // /me/ to fill those in.
      user: {
        id: claims.user_id,
        email: "",
        display_name: "",
        role: claims.role ?? "STAFF",
        // Placeholder — /me/ (fetched right after by useMe/useLogin) backfills
        // the real preference. "BOTH" matches the backend model's default.
        language_preference: "BOTH",
        is_active: true,
        created_at: "",
      },
      role: claims.role ?? null,
      isHydrated: true,
    });
  },

  setUser: (u) => set({ user: u, role: u.role }),
}));

export const useIsAuthenticated = () => useAuthStore((s) => s.user !== null);
